// Copyright (c) 2024-2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

/* global Intl */
import { useMemo } from 'react';
import i18next from 'i18next';
import { de as deDateFnsLocale, enUS as enDateFnsLocale, nl as nlDateFnsLocale } from 'date-fns/locale';
import enTranslations from './translations/en.json';
import deTranslations from './translations/de.json';
import nlTranslations from './translations/nl.json';

// Labels stay in each language's own name so users can find theirs regardless of the active language.
// Every date and number formatter resolves its locale from here, so adding a language is a single entry.
// `intlLocale` is the default region, used when the client has no country.
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', intlLocale: 'en-US', dateFnsLocale: enDateFnsLocale, translations: enTranslations },
  { code: 'de', label: 'Deutsch', intlLocale: 'de-DE', dateFnsLocale: deDateFnsLocale, translations: deTranslations },
  {
    code: 'nl',
    label: 'Nederlands',
    intlLocale: 'nl-NL',
    dateFnsLocale: nlDateFnsLocale,
    translations: nlTranslations,
  },
];

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((language) => language.code);

// Used when the user has no language set, the language is unsupported, or a key is missing in it.
export const FALLBACK_LANGUAGE_CODE = 'de';

// Own instance, so the shared components' global i18next stays untouched. `nonExplicitSupportedLngs`
// lets regional locales through: 'de-CH' resolves its translations as de-CH → de → en.
const i18n = i18next.createInstance();
i18n.init({
  resources: Object.fromEntries(
    SUPPORTED_LANGUAGES.map((language) => [language.code, { translation: language.translations }]),
  ),
  lng: FALLBACK_LANGUAGE_CODE,
  fallbackLng: FALLBACK_LANGUAGE_CODE,
  supportedLngs: SUPPORTED_LANGUAGE_CODES,
  nonExplicitSupportedLngs: true,
  // Keys are flat and placeholders are replaced by the callers, so no nesting or interpolation.
  keySeparator: false,
  nsSeparator: false,
  interpolation: { escapeValue: false },
  initImmediate: false,
});

/**
 * Canonical BCP 47 form of a locale (e.g. 'de-ch' → 'de-CH'), or undefined when it is not a valid locale.
 *
 * @param {string} [locale]
 * @returns {string|undefined}
 */
function canonicalizeLocale(locale) {
  try {
    return Intl.getCanonicalLocales(locale)[0];
  } catch {
    return undefined;
  }
}

function getBaseLanguageCode(languageCode) {
  return languageCode ? i18n.services.languageUtils.getLanguagePartFromCode(languageCode) : '';
}

/**
 * Finds the supported language for a language code or full locale (e.g. 'nl', 'nl-BE'),
 * falling back to German for unknown or missing codes.
 *
 * @param {string} [languageCode]
 * @returns {typeof SUPPORTED_LANGUAGES[number]}
 */
function getLanguageConfig(languageCode) {
  const baseLanguageCode = getBaseLanguageCode(languageCode);
  return (
    SUPPORTED_LANGUAGES.find((language) => language.code === baseLanguageCode) ??
    SUPPORTED_LANGUAGES.find((language) => language.code === FALLBACK_LANGUAGE_CODE)
  );
}

// Region of the signed-in user's client (e.g. 'CH'), set by AuthContext once the user is loaded.
let region;

/**
 * Sets the region used for number and date formatting, from the client's `address.country`.
 *
 * @param {string} [country] - Country code such as 'CH'; empty clears it.
 */
export function setRegion(country) {
  region = country || undefined;
}

/**
 * BCP 47 locale for Intl / toLocaleString formatting of dates and numbers: the language with the client's
 * region ('de' + 'CH' → 'de-CH'), else the language's default ('de-DE').
 *
 * @param {string} [languageCode] - App language code (e.g. 'de', 'nl').
 * @returns {string} Locale such as 'de-CH' or 'nl-NL'.
 */
export function getIntlLocale(languageCode) {
  const language = getLanguageConfig(languageCode);
  return (region && canonicalizeLocale(`${language.code}-${region}`)) || language.intlLocale;
}

/**
 * date-fns locale for `format` and date pickers. date-fns has no Swiss or Belgian variants,
 * so this is per language.
 *
 * @param {string} [languageCode] - App language code or full locale (e.g. 'de', 'nl-BE').
 * @returns {import('date-fns').Locale}
 */
export function getDateFnsLocale(languageCode) {
  return getLanguageConfig(languageCode).dateFnsLocale;
}

/**
 * Picks the first browser locale whose language is supported, region included (e.g. 'de-CH'),
 * falling back to German. Used before sign-in, when there is no user document to read the language from.
 *
 * @param {readonly string[]} [browserLanguages] - Preferred languages, most preferred first (e.g. ['nl-BE', 'en']).
 * @returns {string} Locale such as 'nl-BE' or 'en'.
 */
export function getBrowserLanguageCode(browserLanguages = navigator.languages ?? [navigator.language]) {
  return i18n.services.languageUtils.getBestMatchFromCodes(browserLanguages.filter(Boolean)) ?? FALLBACK_LANGUAGE_CODE;
}

// `t` stays the same between renders until the language changes, so it is safe in effect and callback
// dependency lists. Regional locales ('de-CH') translate with their language.
export const useTranslation = (userLanguageCode = FALLBACK_LANGUAGE_CODE) => {
  return useMemo(
    () => ({
      t: i18n.getFixedT(userLanguageCode || FALLBACK_LANGUAGE_CODE),
      i18n,
    }),
    [userLanguageCode],
  );
};

export default i18n;
